import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from "react";
import { SDropdownV3 } from "../../SDropdownV3";
import { SInputV2 } from "../../SInputV2";
import { SSlider } from "../../SSlider";
const units = [
    "px",
    "%",
    "vw",
    "vh",
    "rem",
    "em"
];
const globalUnits = [
    "auto"
];
const placeholderUnits = [
    "none"
];
const valueTypes = [
    ...units,
    ...globalUnits,
    ...placeholderUnits
];
export const CSSLengthSliderEditor = (props)=>{
    const { value } = props;
    // We know now that literalValue is `css-length` or `css-string`
    const getNextState = (currentState, action)=>{
        const literalValue = currentState.value;
        const literValueIsRaw = literalValue?.unit === "raw";
        const literValueIsGlobal = literalValue?.unit == "none";
        const literalValueHasUnit = literalValue && literalValue?.unit !== "raw" && literalValue?.unit !== "none";
        switch(action.type){
            case "KEY_DOWN":
                {
                    const { event } = action;
                    const key = event.key;
                    const length = literalValue?.value;
                    if (length?.includes("calc") || length?.includes("var")) {
                        return {
                            value: {
                                type: "css-length",
                                unit: "raw",
                                value: length
                            }
                        };
                    }
                    const KeyMap = {
                        ArrowUp: event.shiftKey ? 10 : 1,
                        ArrowDown: event.shiftKey ? -10 : -1
                    };
                    if (key in KeyMap) {
                        const parsedLength = parseFloat(length || "0");
                        const newValue = isNaN(parsedLength) ? 0 : parsedLength + KeyMap[key];
                        if (!literValueIsGlobal) {
                            return {
                                value: {
                                    type: "css-length",
                                    unit: literalValue?.unit || "px",
                                    value: newValue.toString()
                                }
                            };
                        } else {
                            return {
                                value: {
                                    type: "css-length",
                                    unit: "px",
                                    value: newValue.toString()
                                }
                            };
                        }
                    }
                    break;
                }
            case "DROPDOWN_SELECT":
                {
                    const { unit } = action;
                    if (globalUnits.includes(unit)) {
                        return {
                            value: {
                                type: "css-length",
                                value: unit,
                                // Idk why this isn't validating
                                unit: "none"
                            }
                        };
                    }
                    return {
                        value: {
                            type: "css-length",
                            value: (literValueIsGlobal ? "" : literalValue?.value) || "",
                            unit: unit
                        }
                    };
                }
            case "INPUT_CHANGE":
                {
                    const { value } = action;
                    const literalValue = currentState.value;
                    let length = literalValue?.value;
                    let unit = literalValue?.type === "css-length" ? literalValue.unit === "raw" ? null : literalValue.unit : null;
                    // Allow for calc & vars `calc(var(--width) + 10px)`
                    if (value.includes("calc") || value.includes("var")) {
                        return {
                            value: {
                                type: "css-length",
                                unit: "raw",
                                value: value
                            }
                        };
                    }
                    // Snap to units, `12px` -> `12` `px`,
                    // `auto` -> `auto` `none`
                    for (let type of valueTypes){
                        if (value.includes(type)) {
                            unit = type;
                            const parsedLength = value.match(/[a-zA-Z]+|[0-9\.-0-9]+|[%]+/g);
                            if (parsedLength && parsedLength[0]) {
                                if (globalUnits.includes(type)) {
                                    unit = "none";
                                    length = type;
                                } else {
                                    length = parsedLength[0];
                                }
                                return {
                                    value: {
                                        type: "css-length",
                                        value: length,
                                        unit: unit
                                    }
                                };
                            }
                        }
                    }
                    // We already have a unit, so just update the value
                    // this could also result in a weird value but that's
                    // ok for now
                    if (literalValueHasUnit) {
                        return {
                            value: {
                                type: "css-length",
                                value: value,
                                unit: literalValue?.unit
                            }
                        };
                    }
                    // This may result in a weird value if you're typing 'aut' 'px'
                    // See if we can parse the value as a number
                    const parsedValue = parseFloat(value);
                    if (!isNaN(parsedValue)) {
                        return {
                            value: {
                                type: "css-length",
                                value: value,
                                // Picking up the default unit
                                unit: "px"
                            }
                        };
                    }
                    // Leave this as raw to indicate
                    // that we don't know what the value is
                    return {
                        value: {
                            type: "css-length",
                            value: value,
                            unit: "raw"
                        }
                    };
                }
        }
        return {
            value: null
        };
    };
    const dispatch = useCallback((action)=>{
        const nextState = getNextState({
            value: value || null
        }, action);
        if (nextState.value) {
            props.onChangeValue && props.onChangeValue(nextState.value);
        }
    }, [
        getNextState,
        value
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-grayscale-dark rounded-md border border-white/10 grid grid-cols-[1fr,auto,auto] items-center",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-3 border-r border-white/10",
                children: /*#__PURE__*/ _jsx(SSlider, {
                    className: "!h-[30px] !w-full",
                    value: parseInt(value?.value || "0"),
                    onChange: (e)=>dispatch({
                            type: "INPUT_CHANGE",
                            value: e.toString()
                        }),
                    min: 0,
                    max: 100,
                    step: 1
                })
            }),
            /*#__PURE__*/ _jsx(SInputV2, {
                borderless: true,
                tabIndex: 1,
                className: "text-[13px] !px-1 !h-[30px] !w-[38px] !py-0 text-center",
                value: value?.value || "",
                placeholder: "0",
                onChange: (e)=>dispatch({
                        type: "INPUT_CHANGE",
                        value: e.target.value
                    }),
                onKeyDown: (event)=>dispatch({
                        type: "KEY_DOWN",
                        event
                    })
            }),
            /*#__PURE__*/ _jsx(SDropdownV3, {
                className: "!pl-0",
                tabIndex: -1,
                options: valueTypes.filter((e)=>e != "none").map((unit)=>{
                    return {
                        label: unit,
                        value: unit
                    };
                }),
                value: {
                    value: value?.unit,
                    label: value?.unit
                },
                showIcon: false,
                placeholder: "–",
                onChange: (option)=>dispatch({
                        type: "DROPDOWN_SELECT",
                        unit: option.value
                    }),
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-[13px] opacity-70  pr-2",
                    children: value?.unit === "none" ? "-" : value?.unit || "px"
                })
            })
        ]
    });
};
